import { useState, useEffect, type FormEvent } from "react";
import {
  Button,
  Input,
  FormField,
  Alert,
  Heading,
  Spinner,
  useToast,
} from "@/components/ds";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./SettingsPage.module.css";

interface ProfileData {
  readonly name: string;
  readonly email: string;
}

export function SettingsPage() {
  usePageTitle("Settings | TidyRota");
  const { toast } = useToast();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [name, setName] = useState("");
  const [profileError, setProfileError] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    fetch("/api/settings/profile")
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          setProfile(json.data);
          setName(json.data.name);
        }
      });
  }, []);

  async function handleProfileSubmit(event: FormEvent) {
    event.preventDefault();
    setProfileError("");

    if (name.trim().length < 2) {
      setProfileError("Name must be at least 2 characters");
      return;
    }

    setProfileSaving(true);

    try {
      const response = await fetch("/api/settings/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setProfileError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      toast("success", "Profile updated");
    } catch {
      setProfileError("Something went wrong. Please try again.");
    } finally {
      setProfileSaving(false);
    }
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPasswordError("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setPasswordSaving(true);

    try {
      const response = await fetch("/api/settings/password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setPasswordError(data.error?.message ?? "Something went wrong. Please try again.");
        return;
      }

      toast("success", "Password changed");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      setPasswordError("Something went wrong. Please try again.");
    } finally {
      setPasswordSaving(false);
    }
  }

  if (!profile) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-10)" }}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Heading level={1} size="h3">Settings</Heading>

      <form onSubmit={handleProfileSubmit} className={styles.section}>
        <div className={styles.sectionHeader}>Profile</div>

        {profileError && <Alert variant="danger">{profileError}</Alert>}

        <div className={styles.fields}>
          <FormField label="Name" htmlFor="name">
            <Input
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={profileSaving}
            />
          </FormField>

          <FormField label="Email" htmlFor="email">
            <Input
              id="email"
              name="email"
              type="email"
              value={profile.email}
              disabled
            />
          </FormField>

          <div className={styles.actions}>
            <Button type="submit" disabled={profileSaving}>
              {profileSaving ? <Spinner size="sm" /> : "Save changes"}
            </Button>
          </div>
        </div>
      </form>

      <form onSubmit={handlePasswordSubmit} className={styles.section}>
        <div className={styles.sectionHeader}>Change password</div>

        {passwordError && <Alert variant="danger">{passwordError}</Alert>}

        <div className={styles.fields}>
          <FormField label="Current password" htmlFor="current-password">
            <Input
              id="current-password"
              name="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={passwordSaving}
            />
          </FormField>

          <FormField label="New password" htmlFor="new-password">
            <Input
              id="new-password"
              name="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={passwordSaving}
            />
          </FormField>

          <FormField label="Confirm new password" htmlFor="confirm-password">
            <Input
              id="confirm-password"
              name="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={passwordSaving}
            />
          </FormField>

          <div className={styles.actions}>
            <Button type="submit" disabled={passwordSaving}>
              {passwordSaving ? <Spinner size="sm" /> : "Change password"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
